import type { FigureVM } from "./model";

/** A figure the way it is written on screen: 3.9×, 18%, 1,240, 2 pts. */
export function figureText(f: FigureVM): string {
  switch (f.unit) {
    case "ratio":
      return timesText(f.value);
    case "percent":
      return `${round(f.value)}%`;
    case "points":
      return `${round(f.value)} pts`;
    case "count":
      return Math.round(f.value).toLocaleString("en");
  }
}

/** A ratio as the server's sentences say it: two decimals at most, and none for a whole number: 2.79×, 3×. */
export function timesText(v: number): string {
  return `${v.toLocaleString("en", { maximumFractionDigits: 2 })}×`;
}

/**
 * A currency's earned for every one spent, as the layer worked it out. The card carries that figure; a card
 * stored before it did gets the layer's own two decimals. Null when nothing was spent: there is no ratio to show.
 */
export function flowRatio(flow: {
  earned: number;
  spent: number;
  earnedPerSpent?: number;
}): number | null {
  if (flow.earnedPerSpent !== undefined) return flow.earnedPerSpent;
  return flow.spent > 0 ? Math.round((flow.earned / flow.spent) * 100) / 100 : null;
}

/** One decimal at most, and none for a whole number: 3.9, but 2. */
function round(n: number): string {
  return n.toLocaleString("en", { maximumFractionDigits: 1 });
}

/** Time between two instants, as 0:12, or 1:02:09 from an hour on. Never negative. */
export function clock(fromMs: number, toMs: number): string {
  const total = Math.max(0, Math.round((toMs - fromMs) / 1000));
  const pad = (n: number) => String(n).padStart(2, "0");
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  return hours > 0
    ? `${String(hours)}:${pad(minutes)}:${pad(seconds)}`
    : `${String(minutes)}:${pad(seconds)}`;
}

/** 14:05, in the viewer's own time. */
export function timeOfDay(ms: number): string {
  return new Date(ms).toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

/** 27 Oct. */
export function dayMonth(ms: number): string {
  return new Date(ms).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}
